import { ChangeEventArgs, DropDownListComponent } from '@syncfusion/ej2-react-dropdowns'
import { BeforeUploadEventArgs, UploaderComponent, UploadingEventArgs } from '@syncfusion/ej2-react-inputs'
import React, { useRef, useState } from 'react'

import { useAppSelector } from '../../../hooks/reduxHooks.tsx'
import { useOidcAccessToken, useOidcUser } from '@axa-fr/react-oidc'
import { protectedResources } from '../../../global/config.tsx'
import { OidcUserExtendedInfo } from '../../../types/OidcUserExtendedInfo.tsx'
import CustomCard from '../../../components/CustomCard.tsx'
import { NwFileType } from '../../../types/settingTypes/NwFileType.tsx'

export const DocumentUpload = () => {
  const fileTypesResponse: NwFileType[] | undefined = useAppSelector((state) => state.setting.fileTypeList)

  const uploaderRef = useRef<UploaderComponent | null>(null)
  const selectedFileType = useRef<number>(-1)

  const { accessToken } = useOidcAccessToken()
  const { oidcUser } = useOidcUser<OidcUserExtendedInfo>()

  const [selectedFileTypeErr, setSelectedFileTypeErr] = useState<boolean>(false)

  const asyncSettings = {
    chunkSize: 102400,
    retryAfterDelay: 3000,
    retryCount: 5,
    saveUrl: protectedResources.apiList.createFile,
  }

  const uploaderBtnLabels = {
    buttons: {
      // browse: 'Datei(en) wählen',
      clear: 'Entfernen',
      upload: 'Hochladen',
    },
  }

  // Formatiere bytes zu lesbarem String
  function formatBytes(bytes: number) {
    if (!+bytes) return '0 Bytes'

    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`
  }

  const handleDdChange = (e: ChangeEventArgs) => {
    const val = e.itemData as NwFileType
    selectedFileType.current = val.id
  }

  const uploaderTemplate: React.FC<any> = (props: any) => {
    return (
      <>
        <span className="e-file-container" style={{ paddingBottom: '15px' }}>
          <span className="e-file-name">{props.name}</span>
          <span className="e-file-type"> </span>
          <br></br>
          <span className="e-file-size">{formatBytes(props.size)}</span>
          <span className="e-file-status">{props.status}</span>
          <DropDownListComponent floatLabelType="Always" width={'200px'} placeholder="Dokumentenart wählen" dataSource={fileTypesResponse} fields={{ text: 'fileType', value: 'id' }} change={handleDdChange} />

          {selectedFileTypeErr && <p className="errorMsg">Bitte eine Dokumentenart auswählen.</p>}
        </span>
        <span className="e-icons e-file-remove-btn" onClick={() => uploaderRef.current?.remove(props)} title="Entfernen"></span>
      </>
    )
  }

  const onBeforeUpload = (args: BeforeUploadEventArgs) => {
    if (selectedFileType.current !== -1) {
      setSelectedFileTypeErr(false)
    } else {
      // Upload nicht starten, error msg anzeigen
      setSelectedFileTypeErr(true)
      args.cancel = true
    }
  }

  const onUploadBegin = (args: UploadingEventArgs) => {
    args.customFormData = [{ mimeType: (args.fileData.rawFile as Blob).type }, { sub: oidcUser.sub }, { fileTypeId: selectedFileType.current }]

    if (args.currentRequest) {
      args.currentRequest.setRequestHeader('Authorization', `Bearer ${accessToken}`)
    }
  }
  const onChunkUploadBegin = (args: UploadingEventArgs) => {
    args.customFormData = [{ mimeType: (args.fileData.rawFile as Blob).type }, { sub: oidcUser.sub }, { fileTypeId: selectedFileType.current }]

    if (args.currentRequest) {
      args.currentRequest.setRequestHeader('Authorization', `Bearer ${accessToken}`)
    }
  }

  return (
    <CustomCard header="Dokumente hochladen">
      {fileTypesResponse && (
        <UploaderComponent
          ref={uploaderRef}
          type="file"
          autoUpload={false}
          uploading={onUploadBegin}
          beforeUpload={onBeforeUpload}
          chunkUploading={onChunkUploadBegin}
          asyncSettings={asyncSettings}
          allowedExtensions=""
          multiple={false}
          buttons={uploaderBtnLabels.buttons}
          template={uploaderTemplate}
        />
      )}
    </CustomCard>
  )
}
